<template>
    <modal-side-generic
    sizeClass="medium"
    :title="t('transfers.nacional.reuseBeneficiaryModal.title')"
    :showSearchSlot="getBeneficiariesList.length && !applyErrorState && !showLoader"
    @hideMe="emit('hideMe')">
        <template v-if="getBeneficiariesList.length && !applyErrorState && !showLoader" #top-search-area-slot>
            <!-- Search Input -->
            <input-search
            :inputValue="searchInputValue"
            :params="{placeholder: t('transfers.nacional.reuseBeneficiaryModal.searchPlaceholder')}"
            @input="searchInputValue = $event"/>
        </template>
        <!-- Beneficiaries Table -->
        <div v-if="getBeneficiariesList.length && !applyErrorState && !showLoader" class="content-container">
            <table-generic
            v-if="parsedTableData.length"
            hideDetailButton
            :headerData="tableHeaderData"
            :tableData="parsedTableData"
            :tableStyles="tableStyles"
            @rowClicked="tableRowClickHandler($event)"
            @sortBy="tableSortByHandler($event)"/>
            <!-- Empty State -->
            <empty-state-generic
            v-else
            illustration="emptyState/search"
            :title="t('transfers.nacional.reuseBeneficiaryModal.emptyState.noResults.title')"
            :description="t('transfers.nacional.reuseBeneficiaryModal.emptyState.noResults.description')"/>
        </div>
        <!-- Empty State -->
        <empty-state-generic
        v-if="!getBeneficiariesList.length && !applyErrorState && !showLoader"
        illustration="emptyState/beneficiaries"
        :title="t('transfers.nacional.reuseBeneficiaryModal.emptyState.noBeneficiaries.title')"
        :description="t('transfers.nacional.reuseBeneficiaryModal.emptyState.noBeneficiaries.description')"/>
        <!-- Error State -->
        <empty-state-error
        v-if="applyErrorState && !showLoader"
        :message="errorMessage"/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
        <!-- Loader Generic -->
        <div v-if="showLoader" class="loader-wrapper">
            <loaders-generic/>
        </div>
    </modal-side-generic>
</template>

<script setup>
import InputSearch from '@/components/generic/inputs/Search'
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { accountFormatHelper, ibanFormat } from '@/assets/scripts/strings'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseBeneficiaryRowClicked'])

const props = defineProps({
    transferType: String
})

/* mapActions & mapGetters */
const { requestBeneficiariesList, requestBeneficiariesListByDataType } = mapActions(moduleMapper.TRANSFERS_NACIONAL)
const { getBeneficiariesList } = mapGetters(moduleMapper.TRANSFERS_NACIONAL)

// Generic Data
const showLoader = ref(false)
const applyErrorState = ref(false)
const errorMessage = ref('')
const searchInputValue = ref('')

// Reuse Modal Table
const tableStyles = [{ 'min-width': '50%', width: '50%', 'padding-left': '40px' }, { 'text-align': 'end', 'padding-right': '40px' }]

const tableHeaderData = computed(() => {
    if (props.transferType !== 'naHora') {
        return [
            { label: t('transfers.nacional.reuseBeneficiaryModal.tableHeaders.name'), dataAttr: 'name', showInMobile: true, rowCellType: 'title' },
            { label: t('transfers.nacional.reuseBeneficiaryModal.tableHeaders.account'), dataAttr: 'account', showInMobile: true }
        ]
    }
    return [
        { label: t('transfers.nacional.reuseBeneficiaryModal.tableHeaders.name'), dataAttr: 'name', showInMobile: true, rowCellType: 'title' },
        { label: t('transfers.nacional.reuseBeneficiaryModal.tableHeaders.msisdn'), dataAttr: 'msisdn', showInMobile: true }
    ]
})

const parsedTableData = computed(() => {
    if (props.transferType === 'naHora') {
        return getBeneficiariesList.value
        .filter(beneficiary => beneficiary.name.toLowerCase().includes(searchInputValue.value.toLowerCase())) // Filter beneficiaries by name matching or containing searchInputValue
        .filter(beneficiary => beneficiary.phoneNumber) // Exclude beneficiaries with no phone number
        .map(beneficiary => {
            return [
                { value: beneficiary.id, dataAttr: 'id' },
                { type: 'title', value: beneficiary.name, dataAttr: 'name' },
                { value: beneficiary.phoneNumber, dataAttr: 'msisdn' }
            ]
        })
    }
    return getBeneficiariesList.value
        .filter(beneficiary => beneficiary.name.toLowerCase().includes(searchInputValue.value.toLowerCase())) // Filter beneficiaries by name matching or containing searchInputValue
        .map(beneficiary => {
            let dataToUse

            if (props.transferType === 'otherbank') {
                dataToUse = { value: ibanFormat(beneficiary.genericDestination), dataAttr: 'account' }
            } else {
                dataToUse = { value: beneficiary.genericDestination.length === 14 ? accountFormatHelper(beneficiary.genericDestination) : ibanFormat(beneficiary.genericDestination), dataAttr: 'account' }
            }

            return [
                { value: beneficiary.id, dataAttr: 'id' },
                { type: 'title', value: beneficiary.name, dataAttr: 'name' },
                dataToUse
            ]
        })
})

const tableRowClickHandler = selectedBeneficiary => {
    if (props.transferType === 'otherbank') {
        emit('reuseBeneficiaryRowClicked', {
            genericDestination: selectedBeneficiary.rowData.find(data => data.dataAttr === 'account').value,
            beneficiaryName: selectedBeneficiary.rowData.find(data => data.dataAttr === 'name').value
        })
    } else if (props.transferType === 'naHora') {
        const msisdn = selectedBeneficiary.rowData.find(param => param.dataAttr === 'msisdn').value

        emit('reuseBeneficiaryRowClicked', { genericDestination: msisdn })
    } else {
        const beneficary = getBeneficiariesList.value.find(beneficiary => beneficiary.id === selectedBeneficiary.rowId)

        emit('reuseBeneficiaryRowClicked', { genericDestination: beneficary.genericDestination })
    }

    emit('hideMe')
}

const fetchBeneficiaries = async () => {
	applyErrorState.value = false
    showFeedbackToast.value = false
	showLoader.value = true

    let beneficiariesPayload

    if (props.transferType === 'naHora') {
         beneficiariesPayload = { dataType: 'PHONE_NUMBER' }
    } else if (props.transferType === 'otherbank') {
        beneficiariesPayload = { filterTypes: 'OTHER_BANK' }
    } else {
        beneficiariesPayload = { dataType: 'ACCOUNT_NUMBER_OR_IBAN' }
    }
	try {
        props.transferType !== 'otherbank' ? await requestBeneficiariesListByDataType(beneficiariesPayload) : await requestBeneficiariesList(beneficiariesPayload)
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, fetchBeneficiaries)
	}
}

/*************************************************
*                                                *
*                  Error Handler                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    applyErrorState.value = true
    errorMessage.value = data && data.message ? data.message : t('genericErrorMessage')
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

fetchBeneficiaries()
</script>

<style lang="scss" scoped>
.content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
}

.empty-state-wrapper {
    box-shadow: none;
}

.loader-wrapper {
    width: 100%;
    height: 100%;

	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
